import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Empty, Input, InputNumber, Modal, Popconfirm, Select, Spin, Tooltip, message } from 'antd';
import {
    BankOutlined,
    CalculatorOutlined,
    DeleteOutlined,
    DownOutlined,
    LineChartOutlined,
    LinkOutlined,
    AppstoreOutlined,
    CopyOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined,
    ShopOutlined,
    TeamOutlined,
    ToolOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import { axiosInstance } from '../../config';
import {
    FITTING_KEYS,
    MATERIAL_KEYS,
    calculateCost,
    catalogCost,
    catalogOptions,
    emptyCostItem,
    formatCatalogCost,
    isMonthlyType,
    toPayload,
} from './costCalc';

const FRAGMENT_SUGGESTIONS = ['HB-PRO', 'HB-EXT', 'BN-SUM', 'BN-PRO', 'BN-ZIM', 'BN-NEO', 'BN-JS', 'BN-BS'];

const numberFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const money = (value) => `${numberFormat.format(value)} ₽`;
const percent = (value) => `${value.toFixed(1).replace('.', ',')}%`;
const range = (min, max, format) =>
    Math.abs(min - max) < 0.005 ? format(min) : `${format(min)} … ${format(max)}`;
const mid = (min, max) => (min + max) / 2;
const average = (min, max) => (min + max) / 2;

const setIn = (obj, path, value) => {
    const [key, ...rest] = path;
    return { ...obj, [key]: rest.length ? setIn(obj?.[key] ?? {}, rest, value) : value };
};

const isDirty = (draft, item) =>
    Boolean(draft) && JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(item));

const marginTone = (profit, margin) => {
    if (profit < 0) return 'bg-rose-50 text-rose-700 ring-rose-200';
    if (margin < 15) return 'bg-amber-50 text-amber-700 ring-amber-200';
    return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
};

const NumberField = ({ label, value, onChange, suffix = '₽', max, hint }) => (
    <div>
        <div className="mb-1 text-xs font-medium text-slate-500">{label}</div>
        <InputNumber
            value={value}
            onChange={onChange}
            min={0}
            max={max}
            precision={2}
            decimalSeparator=","
            addonAfter={suffix}
            style={{ width: '100%' }}
        />
        {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
    </div>
);

const EmptyCatalogHint = ({ typeLabel }) => (
    <div className="mt-1 text-[11px] text-slate-400">
        Нет позиций «{typeLabel}».{' '}
        <Link to="/cost/materials" className="text-indigo-500 hover:text-indigo-600">
            Добавить в справочник
        </Link>
    </div>
);

const CatalogSelect = ({ catalog, type, value, onChange, placeholder }) => {
    const options = catalogOptions(catalog, type);
    return (
        <>
            <Select
                allowClear
                showSearch
                optionFilterProp="label"
                value={value || undefined}
                onChange={(next) => onChange(next || '')}
                placeholder={placeholder}
                style={{ width: '100%' }}
                options={options}
                notFoundContent="Ничего не найдено"
            />
            {options.length === 0 && <EmptyCatalogHint typeLabel={placeholder} />}
        </>
    );
};

const CatalogDividedField = ({ label, catalog, type, value, unitCost, onChange, unitHint, placeholder }) => (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs font-medium text-slate-500">
            <span>{label}</span>
            <span className="font-semibold text-slate-700">{money(unitCost)} / шт</span>
        </div>
        <div className="mb-2">
            <CatalogSelect
                catalog={catalog}
                type={type}
                value={value?.item_id}
                onChange={(next) => onChange(['item_id'], next)}
                placeholder={placeholder}
            />
        </div>
        <div className="flex items-center gap-2">
            <div className="flex-1 rounded-lg border border-dashed border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700">
                {value?.item_id ? `${money(unitCost)} / шт` : '—'}
            </div>
            <span className="text-slate-400">÷</span>
            <Tooltip title={unitHint}>
                <InputNumber
                    value={value?.divider}
                    onChange={(next) => onChange(['divider'], next)}
                    min={0.01}
                    precision={2}
                    decimalSeparator=","
                    addonAfter="шт"
                    style={{ flex: 1, minWidth: 110 }}
                />
            </Tooltip>
        </div>
    </div>
);

const CatalogPickField = ({ label, catalog, type, value, onChange, placeholder, hint }) => (
    <div>
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs font-medium text-slate-500">
            <span>{label}</span>
            {value ? (
                <span className="font-semibold text-slate-700">
                    {formatCatalogCost(type, catalogCost(catalog, value))}
                    {isMonthlyType(type) ? ' / шт' : ''}
                </span>
            ) : null}
        </div>
        <CatalogSelect
            catalog={catalog}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
        />
        {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
    </div>
);

const SubsectionTitle = ({ children, total }) => (
    <div className="flex items-center justify-between border-b border-slate-100 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <span>{children}</span>
        {total && <span className="normal-case tracking-normal text-slate-600">{total}</span>}
    </div>
);

const Block = ({ icon, title, accent, total, className = '', children }) => (
    <section className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
        <header className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
                <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}>{icon}</span>
                <h3 className="m-0 text-sm font-semibold text-slate-800">{title}</h3>
            </div>
            {total && (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    {total}
                </span>
            )}
        </header>
        <div className="space-y-3">{children}</div>
    </section>
);

const BREAKDOWN = [
    { key: 'production', label: 'Изготовление', color: 'bg-sky-500' },
    { key: 'ozon', label: 'OZON', color: 'bg-indigo-500' },
    { key: 'tax', label: 'Налог', color: 'bg-amber-500' },
    { key: 'employee', label: 'Сотрудники', color: 'bg-fuchsia-500' },
    { key: 'profit', label: 'Чистая прибыль', color: 'bg-emerald-500' },
];

const PriceBreakdown = ({ calc }) => {
    const values = {
        production: calc.production,
        ozon: calc.worst.ozon,
        tax: calc.worst.tax,
        employee: calc.employee,
        profit: Math.max(calc.worst.profit, 0),
    };
    const total = Math.max(
        calc.price,
        values.production + values.ozon + values.tax + values.employee + values.profit
    );
    if (total <= 0) return null;

    return (
        <div>
            <div className="mb-2 text-xs font-medium text-slate-500">
                Структура цены (при максимальной логистике)
            </div>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
                {BREAKDOWN.map(({ key, color, label }) =>
                    values[key] > 0 ? (
                        <Tooltip key={key} title={`${label}: ${money(values[key])}`}>
                            <div className={color} style={{ width: `${(values[key] / total) * 100}%` }} />
                        </Tooltip>
                    ) : null
                )}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {BREAKDOWN.map(({ key, color, label }) => (
                    <span key={key} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                        <span className={`h-2 w-2 rounded-full ${color}`} />
                        {label} · {percent((values[key] / total) * 100)}
                    </span>
                ))}
            </div>
        </div>
    );
};

const ResultTile = ({ label, value, tone = 'text-slate-900', sub }) => (
    <div className="rounded-xl bg-slate-50 p-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
        <div className={`mt-1 text-base font-semibold ${tone}`}>{value}</div>
        {sub && <div className="mt-0.5 text-[11px] text-slate-400">{sub}</div>}
    </div>
);

const CostRow = ({
    item,
    value,
    catalog,
    dirty,
    expanded,
    saving,
    onToggle,
    onChange,
    onSave,
    onReset,
    onCopy,
    onDelete,
}) => {
    const calc = useMemo(() => calculateCost(value, catalog), [value, catalog]);
    const { worst, best } = calc;
    const avgProfit = mid(worst.profit, best.profit);
    const avgMargin = mid(worst.margin, best.margin);
    const tone = marginTone(avgProfit, avgMargin);
    const materialsTotal = MATERIAL_KEYS.reduce((sum, key) => sum + calc.productionParts[key], 0);
    const field = (path) => (next) => onChange(path, next);
    const divided = (key) => (path, next) => onChange(['production', key, ...path], next);

    return (
        <div
            className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-shadow hover:shadow-md ${
                dirty ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-slate-200'
            }`}
        >
            <div
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(event) => event.key === 'Enter' && onToggle()}
                className="grid cursor-pointer grid-cols-2 items-center gap-3 px-4 py-3 md:grid-cols-[minmax(0,2.2fr)_repeat(4,minmax(0,1fr))_auto]"
            >
                <div className="col-span-2 flex min-w-0 items-center gap-3 md:col-span-1">
                    <DownOutlined
                        className={`text-xs text-slate-400 transition-transform ${expanded ? '' : '-rotate-90'}`}
                    />
                    <span className="rounded-lg bg-slate-900 px-2.5 py-1 font-mono text-xs font-semibold text-white">
                        {value.fragment || '—'}
                    </span>
                    <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-800">
                            {value.name || 'Без названия'}
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-slate-400">
                            <LinkOutlined />
                            Артикулов: {item.matched_count ?? 0}
                            {dirty && <span className="ml-2 font-medium text-indigo-500">● не сохранено</span>}
                        </div>
                    </div>
                </div>
                <ResultTile label="Цена" value={money(calc.price)} />
                <ResultTile label="Изготовление" value={money(calc.production)} />
                <ResultTile
                    label="Прибыль"
                    value={money(avgProfit)}
                    tone={avgProfit < 0 ? 'text-rose-600' : 'text-emerald-600'}
                />
                <div className="flex flex-col justify-center rounded-xl bg-slate-50 p-3">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Маржа</div>
                    <span className={`mt-1 w-fit rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${tone}`}>
                        {percent(avgMargin)}
                    </span>
                </div>
                <div
                    className="col-span-2 flex justify-end gap-1 md:col-span-1"
                    onClick={(event) => event.stopPropagation()}
                >
                    {dirty && (
                        <>
                            <Tooltip title="Отменить изменения">
                                <Button icon={<UndoOutlined />} onClick={onReset} disabled={saving} />
                            </Tooltip>
                            <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving}>
                                Сохранить
                            </Button>
                        </>
                    )}
                    <Tooltip title="Создать копию">
                        <Button icon={<CopyOutlined />} onClick={onCopy} />
                    </Tooltip>
                    <Popconfirm
                        title={`Удалить «${item.fragment}»?`}
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                        onConfirm={onDelete}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-slate-100 bg-slate-50/70 p-4">
                    <div className="mb-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)]">
                        <div>
                            <div className="mb-1 text-xs font-medium text-slate-500">Фрагмент артикула</div>
                            <Input
                                value={value.fragment}
                                onChange={(event) => onChange(['fragment'], event.target.value.toUpperCase())}
                                placeholder="HB-PRO"
                                className="font-mono"
                            />
                        </div>
                        <div>
                            <div className="mb-1 text-xs font-medium text-slate-500">Название типа товара</div>
                            <Input
                                value={value.name}
                                onChange={(event) => onChange(['name'], event.target.value)}
                                placeholder="Например: Хоба PRO"
                            />
                        </div>
                        <NumberField label="Цена продавца" value={value.price} onChange={field(['price'])} />
                    </div>

                    <div className="grid gap-4 xl:grid-cols-12">
                        <Block
                            className="xl:col-span-12"
                            icon={<ToolOutlined />}
                            accent="bg-sky-100 text-sky-600"
                            title="Изготовление"
                            total={money(calc.production)}
                        >
                            <div className="grid gap-5 lg:grid-cols-3">
                                <div className="space-y-3">
                                    <SubsectionTitle total={money(materialsTotal)}>Материалы</SubsectionTitle>
                                    {MATERIAL_KEYS.map((key, index) => (
                                        <CatalogDividedField
                                            key={key}
                                            catalog={catalog}
                                            type="material"
                                            label={`Материал ${index + 1}`}
                                            value={value.production?.[key]}
                                            unitCost={calc.productionParts[key]}
                                            onChange={divided(key)}
                                            unitHint="Сколько изделий выходит из единицы материала"
                                            placeholder="Материал"
                                        />
                                    ))}
                                </div>
                                <div className="space-y-3">
                                    <SubsectionTitle>Обработка и помещение</SubsectionTitle>
                                    <CatalogDividedField
                                        catalog={catalog}
                                        type="cutting"
                                        label="Резка"
                                        value={value.production?.cutting}
                                        unitCost={calc.productionParts.cutting}
                                        onChange={divided('cutting')}
                                        unitHint="На сколько изделий делится стоимость резки"
                                        placeholder="Резка"
                                    />
                                    <CatalogDividedField
                                        catalog={catalog}
                                        type="printing"
                                        label="Печать"
                                        value={value.production?.printing}
                                        unitCost={calc.productionParts.printing}
                                        onChange={divided('printing')}
                                        unitHint="На сколько изделий делится стоимость печати"
                                        placeholder="Печать"
                                    />
                                    <CatalogPickField
                                        catalog={catalog}
                                        type="premises"
                                        label="Помещение"
                                        value={value.production?.premises_id}
                                        onChange={(next) => onChange(['production', 'premises_id'], next)}
                                        placeholder="Помещение"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <SubsectionTitle total={money(calc.fittings)}>Фурнитура</SubsectionTitle>
                                    {FITTING_KEYS.map((key, index) => (
                                        <CatalogDividedField
                                            key={key}
                                            catalog={catalog}
                                            type="fittings"
                                            label={`Фурнитура ${index + 1}`}
                                            value={value.production?.[key]}
                                            unitCost={calc.productionParts[key]}
                                            onChange={divided(key)}
                                            unitHint="Сколько изделий выходит из одной штуки фурнитуры"
                                            placeholder="Фурнитура"
                                        />
                                    ))}
                                </div>
                            </div>
                        </Block>

                        <Block
                            className="xl:col-span-6"
                            icon={<ShopOutlined />}
                            accent="bg-indigo-100 text-indigo-600"
                            title="OZON"
                            total={range(best.ozon, worst.ozon, money)}
                        >
                            <CatalogPickField
                                catalog={catalog}
                                type="commission"
                                label="Комиссия / категория"
                                value={value.ozon?.commission_id}
                                onChange={(next) => onChange(['ozon', 'commission_id'], next)}
                                placeholder="Категория товара"
                                hint={calc.commission ? money(calc.commission) : undefined}
                            />
                            <CatalogPickField
                                catalog={catalog}
                                type="acquiring"
                                label="Эквайринг"
                                value={value.ozon?.acquiring_id}
                                onChange={(next) => onChange(['ozon', 'acquiring_id'], next)}
                                placeholder="Эквайринг"
                                hint={calc.acquiring ? money(calc.acquiring) : undefined}
                            />
                            <CatalogPickField
                                catalog={catalog}
                                type="processing"
                                label="Обработка отправления"
                                value={value.ozon?.processing_id}
                                onChange={(next) => onChange(['ozon', 'processing_id'], next)}
                                placeholder="Тип обработки"
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <NumberField
                                    label="Логистика min"
                                    value={value.ozon?.logistics_min}
                                    onChange={field(['ozon', 'logistics_min'])}
                                />
                                <NumberField
                                    label="Логистика max"
                                    value={value.ozon?.logistics_max}
                                    onChange={field(['ozon', 'logistics_max'])}
                                />
                            </div>
                            <NumberField
                                label="Последняя миля"
                                value={value.ozon?.last_mile}
                                onChange={field(['ozon', 'last_mile'])}
                            />
                        </Block>

                        <div className="grid gap-4 md:grid-cols-2 xl:col-span-6">
                            <Block
                                icon={<BankOutlined />}
                                accent="bg-amber-100 text-amber-600"
                                title="Налог"
                                total={range(best.tax, worst.tax, money)}
                            >
                                <div>
                                    <div className="mb-1 text-xs font-medium text-slate-500">Налоговая база</div>
                                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700">
                                        {range(worst.taxBase, best.taxBase, money)}
                                    </div>
                                    <div className="mt-1 text-[11px] text-slate-400">
                                        Цена − OZON − Изготовление
                                    </div>
                                </div>
                                <CatalogPickField
                                    catalog={catalog}
                                    type="tax"
                                    label="Налог"
                                    value={value.tax_id}
                                    onChange={field(['tax_id'])}
                                    placeholder="Налоговая ставка"
                                />
                            </Block>
                            <Block
                                icon={<TeamOutlined />}
                                accent="bg-fuchsia-100 text-fuchsia-600"
                                title="Сотрудники"
                            >
                                <CatalogPickField
                                    catalog={catalog}
                                    type="employee"
                                    label="Средняя стоимость изделия"
                                    value={value.employee_id}
                                    onChange={field(['employee_id'])}
                                    placeholder="Сотрудник"
                                />
                            </Block>
                        </div>

                        <Block
                            className="xl:col-span-12"
                            icon={<LineChartOutlined />}
                            accent="bg-emerald-100 text-emerald-600"
                            title="Результат"
                        >
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                <ResultTile
                                    label="Чистая прибыль"
                                    value={range(worst.profit, best.profit, money)}
                                    tone={worst.profit < 0 ? 'text-rose-600' : 'text-emerald-600'}
                                    sub="min при макс. логистике · max при мин. логистике"
                                />
                                <ResultTile
                                    label="Маржинальность"
                                    value={range(worst.margin, best.margin, percent)}
                                    tone={worst.profit < 0 ? 'text-rose-600' : 'text-emerald-600'}
                                    sub="Чистая прибыль / Цена продавца"
                                />
                                <ResultTile
                                    label="Все расходы"
                                    value={range(
                                        calc.price - best.profit,
                                        calc.price - worst.profit,
                                        money
                                    )}
                                />
                                <ResultTile label="Цена продавца" value={money(calc.price)} />
                            </div>
                            <PriceBreakdown calc={calc} />
                        </Block>
                    </div>
                </div>
            )}
        </div>
    );
};

const nextFragment = (base, used) => {
    const root = String(base || 'COPY').replace(/-\d+$/, '') || 'COPY';
    let n = 2;
    let candidate = `${root}-${n}`;
    while (used.has(candidate)) {
        n += 1;
        candidate = `${root}-${n}`;
    }
    return candidate;
};

const CreateModal = ({ open, items, source, onCancel, onCreate }) => {
    const [fragment, setFragment] = useState('');
    const [name, setName] = useState('');
    const [templateId, setTemplateId] = useState(null);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (!open) return;
        const used = new Set(items.map((item) => item.fragment));
        if (source) {
            setFragment(nextFragment(source.fragment, used));
            setName(source.name ? `${source.name} (копия)` : '');
            setTemplateId(source.id);
            return;
        }
        setFragment('');
        setName('');
        setTemplateId(null);
    }, [open, source]);

    const usedFragments = new Set(items.map((item) => item.fragment));
    const normalized = fragment.trim().toUpperCase();

    const submit = async () => {
        if (!normalized) return;
        setCreating(true);
        try {
            await onCreate(normalized, name, templateId);
        } finally {
            setCreating(false);
        }
    };

    return (
        <Modal
            open={open}
            title={source ? `Копия «${source.fragment}»` : 'Новый тип товара'}
            okText={source ? 'Создать копию' : 'Добавить'}
            cancelText="Отмена"
            onCancel={onCancel}
            onOk={submit}
            confirmLoading={creating}
            okButtonProps={{ disabled: !normalized || usedFragments.has(normalized) }}
            destroyOnClose
        >
            <div className="space-y-4 pt-2">
                <div>
                    <div className="mb-1 text-xs font-medium text-slate-500">Фрагмент артикула</div>
                    <Input
                        autoFocus
                        value={fragment}
                        onChange={(event) => setFragment(event.target.value.toUpperCase())}
                        onPressEnter={submit}
                        placeholder="Например: HB-PRO"
                        className="font-mono"
                        status={usedFragments.has(normalized) ? 'error' : undefined}
                    />
                    {usedFragments.has(normalized) && (
                        <div className="mt-1 text-xs text-rose-500">Такой фрагмент уже есть</div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {FRAGMENT_SUGGESTIONS.filter((value) => !usedFragments.has(value)).map((value) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setFragment(value)}
                                className={`rounded-full border px-2.5 py-0.5 font-mono text-xs transition-colors ${
                                    normalized === value
                                        ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                                        : 'border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-500'
                                }`}
                            >
                                {value}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <div className="mb-1 text-xs font-medium text-slate-500">Название</div>
                    <Input
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Например: Хоба PRO"
                    />
                </div>
                {source ? (
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-sm text-slate-600">
                        Все поля копируются из <span className="font-mono font-semibold">{source.fragment}</span>
                        {source.name ? ` — ${source.name}` : ''}. Дальше можно поменять только отличающиеся позиции.
                    </div>
                ) : (
                    items.length > 0 && (
                        <div>
                            <div className="mb-1 text-xs font-medium text-slate-500">Скопировать значения из</div>
                            <Select
                                allowClear
                                value={templateId}
                                onChange={setTemplateId}
                                placeholder="Пустая запись"
                                style={{ width: '100%' }}
                                options={items.map((item) => ({
                                    value: item.id,
                                    label: `${item.fragment}${item.name ? ` — ${item.name}` : ''}`,
                                }))}
                            />
                        </div>
                    )
                )}
            </div>
        </Modal>
    );
};

const ProductCost = () => {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [expanded, setExpanded] = useState(() => new Set());
    const [loading, setLoading] = useState(false);
    const [savingIds, setSavingIds] = useState(() => new Set());
    const [search, setSearch] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [copySource, setCopySource] = useState(null);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const [itemsResponse, catalogResponse] = await Promise.all([
                axiosInstance.get('/v1/cost/items'),
                axiosInstance.get('/v1/cost/catalog'),
            ]);
            setItems(itemsResponse.data || []);
            setCatalog(catalogResponse.data || []);
            setDrafts({});
        } catch (err) {
            console.error('Error fetching cost items:', err);
            message.error('Не удалось загрузить себестоимость');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const dirtyIds = useMemo(
        () => items.filter((item) => isDirty(drafts[item.id], item)).map((item) => item.id),
        [items, drafts]
    );

    useEffect(() => {
        if (!dirtyIds.length) return undefined;
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirtyIds.length]);

    const visibleItems = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return items;
        return items.filter((item) => {
            const value = drafts[item.id] || item;
            return `${value.fragment} ${value.name}`.toLowerCase().includes(query);
        });
    }, [items, drafts, search]);

    const averageMargin = useMemo(() => {
        const priced = items
            .map((item) => calculateCost(drafts[item.id] || item, catalog))
            .filter((calc) => calc.price > 0);
        if (!priced.length) return null;
        return (
            priced.reduce((sum, calc) => sum + mid(calc.worst.margin, calc.best.margin), 0) / priced.length
        );
    }, [items, drafts, catalog]);

    const toggle = (id) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const setSaving = (id, saving) =>
        setSavingIds((prev) => {
            const next = new Set(prev);
            if (saving) next.add(id);
            else next.delete(id);
            return next;
        });

    const handleChange = (item, path, value) =>
        setDrafts((prev) => ({ ...prev, [item.id]: setIn(prev[item.id] || item, path, value) }));

    const handleReset = (id) =>
        setDrafts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });

    const handleSave = async (item) => {
        const payload = toPayload(drafts[item.id] || item);
        if (!payload.fragment) {
            message.warning('Укажите фрагмент артикула');
            return;
        }
        setSaving(item.id, true);
        try {
            const response = await axiosInstance.put(`/v1/cost/items/${item.id}`, payload);
            setItems((prev) => prev.map((row) => (row.id === item.id ? response.data : row)));
            handleReset(item.id);
            message.success(`«${payload.fragment}» сохранено`);
        } catch (err) {
            console.error('Error saving cost item:', err);
            message.error(err.response?.data?.detail || 'Не удалось сохранить');
        } finally {
            setSaving(item.id, false);
        }
    };

    const handleSaveAll = () =>
        Promise.all(items.filter((item) => dirtyIds.includes(item.id)).map(handleSave));

    const handleDelete = async (item) => {
        try {
            await axiosInstance.delete(`/v1/cost/items/${item.id}`);
            setItems((prev) => prev.filter((row) => row.id !== item.id));
            handleReset(item.id);
            message.success(`«${item.fragment}» удалено`);
        } catch (err) {
            console.error('Error deleting cost item:', err);
            message.error(err.response?.data?.detail || 'Не удалось удалить');
        }
    };

    const handleCreate = async (fragment, name, templateId) => {
        const template = items.find((item) => item.id === templateId);
        const base = template ? drafts[template.id] || template : emptyCostItem();
        try {
            const response = await axiosInstance.post('/v1/cost/items', {
                ...toPayload(base),
                fragment,
                name,
            });
            setItems((prev) =>
                [...prev, response.data].sort((a, b) => a.fragment.localeCompare(b.fragment))
            );
            setExpanded((prev) => new Set(prev).add(response.data.id));
            setCreateOpen(false);
            setCopySource(null);
            message.success(`«${fragment}» добавлено`);
        } catch (err) {
            console.error('Error creating cost item:', err);
            message.error(err.response?.data?.detail || 'Не удалось добавить');
        }
    };

    return (
        <div className="mx-auto max-w-[1600px] space-y-4">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-6 text-white shadow-lg">
                <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-indigo-500/20 blur-3xl" />
                <div className="relative flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-indigo-200">
                            <CalculatorOutlined /> Юнит-экономика
                        </div>
                        <h1 className="mt-1 text-2xl font-semibold text-white">Себестоимость</h1>
                        <p className="mt-1 max-w-xl text-sm text-slate-300">
                            Расчёт чистой прибыли по типам товаров. Каждый тип привязан к фрагменту артикула.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {dirtyIds.length > 0 && (
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSaveAll}
                                loading={savingIds.size > 0}
                            >
                                Сохранить все ({dirtyIds.length})
                            </Button>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={fetchItems} loading={loading} ghost>
                            Обновить
                        </Button>
                        <Link to="/cost/materials">
                            <Button icon={<AppstoreOutlined />} ghost>
                                Материалы
                            </Button>
                        </Link>
                        <Button
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setCopySource(null);
                                setCreateOpen(true);
                            }}
                        >
                            Добавить тип товара
                        </Button>
                    </div>
                </div>
                <div className="relative mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-white/10 p-3 backdrop-blur">
                        <div className="text-[11px] uppercase tracking-wide text-slate-300">Типов товаров</div>
                        <div className="mt-1 text-xl font-semibold">{items.length}</div>
                    </div>
                    <div className="rounded-2xl bg-white/10 p-3 backdrop-blur">
                        <div className="text-[11px] uppercase tracking-wide text-slate-300">
                            Средняя маржа
                        </div>
                        <div className="mt-1 text-xl font-semibold">
                            {averageMargin === null ? '—' : percent(averageMargin)}
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white/10 p-3 backdrop-blur">
                        <div className="text-[11px] uppercase tracking-wide text-slate-300">Не сохранено</div>
                        <div className="mt-1 text-xl font-semibold">{dirtyIds.length}</div>
                    </div>
                </div>
            </div>

            {items.length > 0 && (
                <Input
                    allowClear
                    prefix={<SearchOutlined className="text-slate-400" />}
                    placeholder="Поиск по фрагменту артикула или названию"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    style={{ maxWidth: 420 }}
                />
            )}

            <Spin spinning={loading}>
                {visibleItems.length > 0 ? (
                    <div className="space-y-3">
                        {visibleItems.map((item) => (
                            <CostRow
                                key={item.id}
                                item={item}
                                value={drafts[item.id] || item}
                                catalog={catalog}
                                dirty={dirtyIds.includes(item.id)}
                                expanded={expanded.has(item.id)}
                                saving={savingIds.has(item.id)}
                                onToggle={() => toggle(item.id)}
                                onChange={(path, value) => handleChange(item, path, value)}
                                onSave={() => handleSave(item)}
                                onReset={() => handleReset(item.id)}
                                onCopy={() => {
                                    setCopySource(drafts[item.id] || item);
                                    setCreateOpen(true);
                                }}
                                onDelete={() => handleDelete(item)}
                            />
                        ))}
                    </div>
                ) : (
                    !loading && (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12">
                            <Empty
                                description={
                                    items.length
                                        ? 'Ничего не найдено'
                                        : 'Добавьте первый тип товара, чтобы рассчитать прибыль'
                                }
                            >
                                {!items.length && (
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setCopySource(null);
                                            setCreateOpen(true);
                                        }}
                                    >
                                        Добавить тип товара
                                    </Button>
                                )}
                            </Empty>
                        </div>
                    )
                )}
            </Spin>

            <CreateModal
                open={createOpen}
                items={items}
                source={copySource}
                onCancel={() => {
                    setCreateOpen(false);
                    setCopySource(null);
                }}
                onCreate={handleCreate}
            />
        </div>
    );
};

export default ProductCost;
